import { useMemo, useState } from 'react'
import { AlertCircle } from 'lucide-react'

import {
  useCorrelation,
  type CorrelationMetric,
  type FeatureBucket,
} from '@/api/endpoints/analytics'
import { Section } from '@/components/ds/Section'
import { Segmented } from '@/components/ui/Segmented'
import { cn } from '@/lib/cn'

import styles from './CorrelationSection.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

export interface CorrelationSectionProps {
  channel: string
  /** Lookback window in days — defaults to 90, server clamps to [7, 365]. */
  days?: number
}

const METRIC_OPTIONS: ReadonlyArray<{ value: CorrelationMetric; label: string; help: string }> = [
  { value: 'views', label: 'Views', help: 'Distribuição da contagem de views por feature' },
  {
    value: 'like_rate',
    label: 'Like rate',
    help: 'Likes / views — só conta vídeos com pelo menos 1 view',
  },
  {
    value: 'comment_rate',
    label: 'Comment rate',
    help: 'Comments / views — só conta vídeos com pelo menos 1 view',
  },
  {
    value: 'retention',
    label: 'Retenção',
    help: 'Average view percentage no snapshot t7d (depende de ANALYTICS_REAL_REQUESTS=1)',
  },
] as const

const FEATURE_LABELS: Record<string, string> = {
  kind: 'Tipo de vídeo',
  theme: 'Tema',
  language: 'Idioma',
  pattern: 'Padrão narrativo',
  hook_words: 'Palavras no hook',
  title_chars: 'Tamanho do título',
  hashtag_count: 'Hashtags',
  closing_words: 'Palavras no encerramento',
  segment_count: 'Segmentos do roteiro',
  total_duration_s: 'Duração total',
  visual_cuts: 'Cortes visuais',
  has_cta: 'Tem CTA',
  has_thumb_highlight: 'Palavra-destaque na thumb',
  engagement_cta: 'Pedido de like/comentário/inscrição',
}

/**
 * "Quais características de vídeo correlacionam com mais views vs. mais
 * likes neste canal?" — agrupa cada vídeo do canal por feature
 * extraível (kind, hook length, hashtag count, has_thumb_highlight, ...)
 * e mostra a distribuição da métrica escolhida por bucket.
 *
 * Mediana é o número headline; p25/p75 expostos pela barra de spread.
 * Buckets com menos amostras que ``LOW_CONFIDENCE_THRESHOLD`` no backend
 * (3 hoje) ganham um badge "low confidence" — operadores não devem tirar
 * conclusões firmes de uma única amostra sortuda.
 */
export function CorrelationSection({ channel, days = 90 }: CorrelationSectionProps) {
  const [metric, setMetric] = useState<CorrelationMetric>('views')
  const query = useCorrelation({ channel, metric, days })

  const subtitle = query.data
    ? `${String(query.data.days)} dias · ${String(query.data.sample_size)} vídeo${query.data.sample_size === 1 ? '' : 's'}`
    : `Janela: ${String(days)} dias`

  const features = useMemo<{ id: string; buckets: FeatureBucket[] }[]>(() => {
    const byFeature = query.data?.by_feature ?? {}
    return Object.entries(byFeature)
      .filter(([, buckets]) => buckets.length > 0)
      .map(([id, buckets]) => ({ id, buckets }))
  }, [query.data])

  return (
    <Section title="Correlação features × métrica" description={subtitle}>
      <div className={styles.shell}>
        <p className={styles.headerHelp}>
          Cada card agrupa os vídeos do canal por uma característica e mostra
          a <strong>mediana</strong> da métrica em cada bucket — ranqueado do
          maior pro menor. A linha abaixo mostra a <strong>faixa central</strong>{' '}
          (onde 50% dos vídeos do bucket ficam, do percentil 25 ao 75): quanto
          mais larga, maior a variação. Buckets com pouca amostra ganham um
          badge <em>low confidence</em>.
        </p>

        <div className={styles.controls}>
          <Segmented<CorrelationMetric>
            aria-label="Métrica para análise"
            options={METRIC_OPTIONS.map((o) => ({ value: o.value, label: o.label, title: o.help }))}
            value={metric}
            onValueChange={setMetric}
            size="sm"
          />
          <p className={styles.metricHelp}>{METRIC_OPTIONS.find((o) => o.value === metric)?.help}</p>
        </div>

        {query.isError ? (
          <QueryErrorNotice
            error={query.error}
            label="a correlação"
            onRetry={() => {
              void query.refetch()
            }}
          />
        ) : query.isPending ? (
          <CorrelationSkeleton />
        ) : query.data.sample_size === 0 ? (
          <div className={styles.empty}>
            {metric === 'retention'
              ? 'Sem dados de retenção ainda. Ative ANALYTICS_REAL_REQUESTS=1 e espere o cron coletar.'
              : 'Sem vídeos publicados na janela com dados desta métrica.'}
          </div>
        ) : features.length === 0 ? (
          <div className={styles.empty}>
            Sem features com dados suficientes para a métrica escolhida.
          </div>
        ) : (
          <div className={styles.grid}>
            {features.map((f) => (
              <FeaturePanel key={f.id} featureId={f.id} buckets={f.buckets} metric={metric} />
            ))}
          </div>
        )}
      </div>
    </Section>
  )
}

// ---------------------------------------------------------------------------
// One panel per feature — buckets sorted by sample count desc.
// Each row shows: bucket label + median (formatted by metric) + samples +
// optional low-confidence badge + horizontal magnitude bar relative to the
// max median in this feature.
// ---------------------------------------------------------------------------

interface FeaturePanelProps {
  featureId: string
  buckets: FeatureBucket[]
  metric: CorrelationMetric
}

function FeaturePanel({ featureId, buckets, metric }: FeaturePanelProps) {
  const label = FEATURE_LABELS[featureId] ?? featureId
  // Magnitude bar uses the max median across buckets in *this* feature.
  // Comparing across features doesn't make sense (themes vs hashtag counts
  // live in different scales), so each panel normalizes locally.
  const maxMedian = useMemo(() => {
    let m = 0
    for (const b of buckets) {
      if (b.median > m) m = b.median
    }
    return m
  }, [buckets])

  return (
    <article className={styles.panel}>
      <header className={styles.panelHead}>
        <h3 className={styles.panelTitle}>{label}</h3>
        <span className={styles.panelKey}>{featureId}</span>
      </header>
      <ul className={styles.bucketList}>
        {buckets.map((b) => (
          <BucketRow key={b.bucket} bucket={b} metric={metric} maxMedian={maxMedian} />
        ))}
      </ul>
    </article>
  )
}

interface BucketRowProps {
  bucket: FeatureBucket
  metric: CorrelationMetric
  maxMedian: number
}

function BucketRow({ bucket, metric, maxMedian }: BucketRowProps) {
  const ratio = maxMedian > 0 ? Math.max(0, Math.min(1, bucket.median / maxMedian)) : 0
  const widthPct = `${String(Math.max(2, ratio * 100))}%`
  return (
    <li className={cn(styles.bucket, bucket.low_confidence && styles.bucketLowConf)}>
      <div className={styles.bucketHeader}>
        <span className={styles.bucketLabel}>{bucket.bucket}</span>
        <span className={styles.bucketMedian}>{formatMetric(bucket.median, metric)}</span>
      </div>
      <div className={styles.bucketBarTrack} aria-hidden>
        <div className={styles.bucketBarFill} style={{ width: widthPct }} />
      </div>
      <div className={styles.bucketFooter}>
        <span
          className={styles.bucketSamples}
          title={`Faixa central (p25 a p75): ${formatMetric(bucket.p25, metric)} a ${formatMetric(bucket.p75, metric)}`}
        >
          {String(bucket.samples)} vídeo{bucket.samples === 1 ? '' : 's'} · faixa{' '}
          {formatMetric(bucket.p25, metric)}–{formatMetric(bucket.p75, metric)}
        </span>
        {bucket.low_confidence ? (
          <span
            className={styles.lowConfBadge}
            title="Poucas amostras: pode ser ruído. Aguarde mais vídeos publicados."
          >
            <AlertCircle aria-hidden />
            low confidence
          </span>
        ) : null}
      </div>
    </li>
  )
}

// ---------------------------------------------------------------------------
// Skeleton — three placeholder panels while the query is pending.
// ---------------------------------------------------------------------------

function CorrelationSkeleton() {
  return (
    <div className={styles.grid} aria-hidden>
      {[0, 1, 2].map((i) => (
        <div key={`sk-${String(i)}`} className={cn(styles.panel, styles.skeletonPanel)}>
          <div className={cn(styles.skeletonShimmer, styles.skeletonTitle)} />
          {[0, 1, 2].map((j) => (
            <div key={`sk-row-${String(i)}-${String(j)}`} className={styles.skeletonRow}>
              <div className={cn(styles.skeletonShimmer, styles.skeletonHeader)} />
              <div className={cn(styles.skeletonShimmer, styles.skeletonBar)} />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatMetric(value: number, metric: CorrelationMetric): string {
  if (metric === 'views') return formatCount(Math.round(value))
  // like_rate, comment_rate, retention all live in [0, 1] — render as %.
  return `${(value * 100).toFixed(2)}%`
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`
  return String(n)
}
